import { Fragment, useState, useCallback, useMemo, useEffect, type RefObject } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  Activity,
  Loader2,
  ExternalLink,
  Eye,
  HelpCircle,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ACTIVITY_EVENT_ICONS,
  ACTIVITY_EVENT_COLORS,
  ACTIVITY_CATEGORY_OPTIONS,
  ACTIVITY_CATEGORY_EMPTY_LABEL,
  getActivityEventLabel,
  getActivityDisplayTitle,
  formatActivityTimestamp,
  parseDateSafe,
  getScoreBandBadgeClass,
  getDateGroupLabel,
  getDateGroupLabelLong,
} from "@/lib/activity-event-utils";
import { HistoryGapMarker } from "@/components/animals/HistoryGapMarker";

interface ActivityItem {
  id: string;
  source: "event" | "task" | "meeting";
  eventType: string;
  title: string;
  message: string | null;
  metadata: unknown;
  statusName: string | null;
  assigneeName: string | null;
  dueDate: string | null;
  createdAt: string;
  occurredAt: string | null;
  actorFirstName: string | null;
}

interface ActivityResponse {
  items: ActivityItem[];
  total: number;
  limit: number;
  offset: number;
}

const PAGE_SIZE = 20;

const OBSERVATION_FAMILY_TYPES = new Set([
  "observation_logged",
  "behavior_alert",
  "observation_added_to_task",
  "observation_added_to_meeting",
]);

interface AnimalActivityTimelineProps {
  animalId: string;
  animalName?: string | null;
  onObservationClick?: (interactionId: string) => void;
  // AF.5: optional controlled category for tab-switch persistence.
  // When provided the parent owns the state; otherwise internal useState is used.
  category?: string;
  onCategoryChange?: (category: string) => void;
  // AP.3: history gap marker
  dataStartDate?: string | null;
  intakeDate?: string | null;
  // Scroll container ref — used as IntersectionObserver root so the sentinel
  // is observed within the clipped card rather than the viewport.
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
}

function possessive(name: string): string {
  return name.endsWith("s") || name.endsWith("S") ? `${name}'` : `${name}'s`;
}

function getItemLink(item: ActivityItem, animalId: string): string | null {
  switch (item.source) {
    case "task":
      return `/tasks/${item.id}`;
    case "meeting":
      return `/meetings/${item.id}`;
    case "event":
      switch (item.eventType) {
        case "behavior_alert":
        case "alert_flag":
          return `/animals/${animalId}?tab=behavior`;
        case "file_upload": {
          const fId = (item.metadata as { fileId?: string } | null)?.fileId;
          return `/animals/${animalId}?tab=files${fId ? `&fileId=${fId}` : ""}`;
        }
        case "location_transfer":
        case "re_intake":
        case "initial_intake":
        case "outcome_cleared":
          return `/animals/${animalId}?tab=stays`;
        case "outcome":
        case "status_change":
        case "adoption_level_change":
        case "observation_added_to_task":
        case "observation_added_to_meeting":
          return null;
        case "eval_status_change":
          return `/animals/${animalId}?tab=behavior`;
        case "observation_logged":
          return `/animals/${animalId}?tab=behavior`;
        default:
          return null;
      }
    default:
      return null;
  }
}

function getItemLinkTooltip(item: ActivityItem): string {
  if (item.source === "task") return "Open task";
  if (item.source === "meeting") return "Open meeting";
  switch (item.eventType) {
    case "file_upload": return "View file";
    case "location_transfer":
    case "re_intake":
    case "initial_intake":
    case "outcome_cleared": return "View stay history";
    case "behavior_alert":
    case "alert_flag":
    case "eval_status_change":
    case "observation_logged": return "Go to behavior tab";
    default: return "View details";
  }
}

function StaysHint({ animalId }: { animalId: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link href={`/animals/${animalId}?tab=stays`} data-testid="link-stays-hint">
          <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/60 cursor-pointer" />
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-[220px] text-xs">
        Full stay history is outlined on the <span className="font-medium">Stays</span> tab
      </TooltipContent>
    </Tooltip>
  );
}

export function AnimalActivityTimeline({
  animalId,
  animalName,
  onObservationClick,
  category: categoryProp,
  onCategoryChange,
  dataStartDate,
  intakeDate,
  scrollContainerRef,
}: AnimalActivityTimelineProps) {
  // AF.5: controlled when parent passes category/onCategoryChange, otherwise internal
  const [categoryInternal, setCategoryInternal] = useState<string>("all");
  const isControlled = categoryProp !== undefined;
  const category = isControlled ? (categoryProp ?? "all") : categoryInternal;
  const setCategory = useCallback((val: string) => {
    if (isControlled) {
      onCategoryChange?.(val);
    } else {
      setCategoryInternal(val);
    }
  }, [isControlled, onCategoryChange]);

  const [extraItems, setExtraItems] = useState<ActivityItem[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    setExtraItems([]);
    setFetchError(false);
    setExhausted(false);
  }, [category]);

  const categoryParam = category !== "all" ? `&category=${category}` : "";

  const { data, isLoading, isError, isPlaceholderData } = useQuery<ActivityResponse>({
    queryKey: ["/api/animals", animalId, "activity", category],
    queryFn: async () => {
      const res = await fetch(`/api/animals/${animalId}/activity?limit=${PAGE_SIZE}&offset=0${categoryParam}`);
      if (!res.ok) throw new Error("Failed to fetch activity");
      return await res.json();
    },
    enabled: !!animalId,
    placeholderData: keepPreviousData,
  });

  const loadedItems = useMemo(() => {
    const firstPage = data?.items ?? [];
    return extraItems.length > 0 ? [...firstPage, ...extraItems] : firstPage;
  }, [data?.items, extraItems]);

  const totalCount = data?.total ?? 0;
  const hasMore = !exhausted && loadedItems.length < totalCount;

  const handleLoadMore = useCallback(async () => {
    const nextOffset = loadedItems.length;
    setIsLoadingMore(true);
    try {
      const res = await fetch(`/api/animals/${animalId}/activity?limit=${PAGE_SIZE}&offset=${nextOffset}${categoryParam}`);
      if (!res.ok) throw new Error("Failed to fetch more activity");
      const pageData: ActivityResponse = await res.json();
      if (pageData.items.length === 0) {
        setExhausted(true);
      } else {
        setExtraItems(prev => [...prev, ...pageData.items]);
      }
    } catch {
      setFetchError(true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [animalId, loadedItems.length, categoryParam]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container || !hasMore || isLoadingMore) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 120) handleLoadMore();
    };
    handleScroll();
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [hasMore, isLoadingMore, handleLoadMore, scrollContainerRef]);

  const title = "Activity Log";
  const emptyLabel = ACTIVITY_CATEGORY_EMPTY_LABEL[category];

  // AP.3: show gap marker when animal's intake predates available BETI data
  const showGapMarker = !!(
    dataStartDate &&
    intakeDate &&
    new Date(intakeDate) < new Date(dataStartDate)
  );

  // AF.1: sticky header — stays visible while the event list scrolls beneath
  const headerEl = (
    <div className="sticky top-0 z-10 bg-card flex items-center gap-2 pt-4 pb-3 mb-0 flex-wrap border-b">
      <Label className="text-xs font-medium uppercase tracking-wider flex items-center gap-1">
        <Activity className="w-3 h-3" /> {title}
      </Label>
      <StaysHint animalId={animalId} />
      {/* AF.3: badge reflects filtered total; dimmed while category is switching */}
      {totalCount > 0 && (
        <Badge
          variant="secondary"
          className={`text-xs transition-opacity ${isPlaceholderData ? "opacity-40" : ""}`}
          data-testid="badge-activity-count"
        >
          {totalCount}
        </Badge>
      )}
      <div className="ml-auto">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-7 text-xs w-40" data-testid="select-activity-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTIVITY_CATEGORY_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="loading-activity">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if ((isError || fetchError) && loadedItems.length === 0) {
    return (
      <div data-testid="activity-timeline-container">
        {headerEl}
        <div className="py-6 text-center text-muted-foreground mt-3" data-testid="text-activity-error">
          <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-destructive/50" />
          <p className="text-sm">Failed to load activity</p>
        </div>
      </div>
    );
  }

  if (loadedItems.length === 0) {
    return (
      <div data-testid="activity-timeline-container">
        {headerEl}
        <div className="py-6 text-center text-muted-foreground mt-3" data-testid="text-no-activity">
          <Activity className="h-6 w-6 mx-auto mb-2 text-muted-foreground/50" />
          <p className="text-sm">
            {emptyLabel ? `No ${emptyLabel} recorded` : "No activity recorded yet"}
          </p>
        </div>
      </div>
    );
  }

  // AF.7: build set of interactionIds already represented by a behavior_alert row.
  // observation_logged rows whose interactionId is in this set are suppressed —
  // the behavior_alert carries the score badge and interaction drawer link.
  const alertCoveredIds = new Set<string>();
  for (const item of loadedItems) {
    if (item.eventType === "behavior_alert") {
      const id = (item.metadata as { interactionId?: string } | null)?.interactionId;
      if (id) alertCoveredIds.add(id);
    }
  }

  // AF.4: track last rendered date group label for divider injection
  let lastDateLabel: string | null = null;

  return (
    <div data-testid="activity-timeline-container">
      {headerEl}
      <div className="rounded-md border mt-3" data-testid="activity-timeline">
        <div className="divide-y list-zebra">
          {loadedItems.map((item) => {
            // AF.7: suppress observation_logged when a behavior_alert covers the same interaction
            const suppressedObsId = item.eventType === "observation_logged"
              ? (item.metadata as { interactionId?: string } | null)?.interactionId ?? null
              : null;
            if (suppressedObsId && alertCoveredIds.has(suppressedObsId)) return null;

            const Icon = ACTIVITY_EVENT_ICONS[item.eventType] || Activity;
            const dotColor = ACTIVITY_EVENT_COLORS[item.eventType] || "bg-muted-foreground";
            const label = getActivityEventLabel(item.eventType, item.source);
            const createdDate = parseDateSafe(item.occurredAt ?? item.createdAt);
            const linkUrl = getItemLink(item, animalId);
            // AF.6: extend score badge to task/meeting observation events.
            // metadata.scoreCode is preferred; fall back to regex on the message
            // for older events that pre-date metadata enrichment.
            const scoreMeta = OBSERVATION_FAMILY_TYPES.has(item.eventType)
              ? (item.metadata as { interactionId?: string; scoreCode?: string; scoreBand?: string } | null)
              : null;
            const scoreCodeFromMeta = scoreMeta?.scoreCode ?? null;
            const scoreCodeFromMessage = !scoreCodeFromMeta && item.message
              ? (item.message.match(/\(([^)]+)\)/)?.[1] ?? null)
              : null;
            const scoreCode = scoreCodeFromMeta ?? scoreCodeFromMessage ?? null;
            const scoreBand = scoreMeta?.scoreBand ?? null;
            const obsInteractionId = (item.eventType === "observation_added_to_task" || item.eventType === "observation_added_to_meeting" || item.eventType === "observation_logged" || item.eventType === "behavior_alert")
              ? (item.metadata as { interactionId?: string } | null)?.interactionId ?? null
              : null;
            const taskMeta = (item.eventType === "observation_added_to_task" || item.eventType === "observation_added_to_meeting")
              ? (item.metadata as { taskId?: string; taskType?: string } | null)
              : null;
            const taskLinkUrl = taskMeta?.taskId
              ? (taskMeta.taskType === "meeting" ? `/meetings/${taskMeta.taskId}` : `/tasks/${taskMeta.taskId}`)
              : null;
            const { displayTitle, subtitle } = getActivityDisplayTitle(
              item.eventType, item.title, item.source, animalName || animalId,
            );

            // AF.4: inject date group header when date changes
            const dateLabel = getDateGroupLabel(createdDate);
            const showDateHeader = dateLabel !== lastDateLabel;
            lastDateLabel = dateLabel;

            return (
              <Fragment key={item.id}>
                {showDateHeader && (
                  <div className="px-3 py-1 text-[10px] font-medium text-muted-foreground/60 bg-muted/40 border-b" data-testid={`date-group-${dateLabel}`}>
                    <span className="sm:hidden">{dateLabel}</span>
                    <span className="hidden sm:inline">{getDateGroupLabelLong(createdDate)}</span>
                  </div>
                )}
                <div className="flex gap-3 px-3 py-2.5" data-testid={`activity-item-${item.id}`}>
                  <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${dotColor} mt-0.5`}>
                    <Icon className="h-3 w-3 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2 flex-wrap">
                      <p className="text-sm leading-tight font-medium" data-testid={`text-activity-title-${item.id}`}>
                        {displayTitle}
                      </p>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                        {label}
                      </Badge>
                      {scoreCode && (
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 font-medium ${getScoreBandBadgeClass(scoreBand)}`} data-testid={`badge-score-${item.id}`}>
                          Score {scoreCode}
                        </Badge>
                      )}
                      {obsInteractionId && onObservationClick ? (
                        // Has a linked interaction — Eye opens the drawer directly
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button onClick={() => onObservationClick(obsInteractionId)} data-testid={`button-observation-${item.id}`}>
                              <Eye className="h-3 w-3 text-muted-foreground/50 hover:text-foreground transition-colors shrink-0" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            {item.eventType === "behavior_alert" ? "View interaction" : "View observation"}
                          </TooltipContent>
                        </Tooltip>
                      ) : OBSERVATION_FAMILY_TYPES.has(item.eventType) ? (
                        // Observation-family event with no stored interactionId — show Eye
                        // with a popover explaining details aren't available + link to the
                        // interactions table filtered to this animal
                        <Popover>
                          <PopoverTrigger asChild>
                            <button data-testid={`button-observation-unavailable-${item.id}`}>
                              <Eye className="h-3 w-3 text-muted-foreground/30 hover:text-muted-foreground transition-colors shrink-0" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent side="top" className="w-64 p-3 space-y-2">
                            <p className="text-sm text-muted-foreground">
                              Individual interaction details are not stored for this entry.
                            </p>
                            <Link
                              href={`/interactions?animalId=${animalId}&startDate=${format(createdDate, "yyyy-MM-dd")}&endDate=${format(createdDate, "yyyy-MM-dd")}`}
                              className="text-sm text-primary underline underline-offset-2 hover:no-underline"
                              data-testid={`link-interactions-animal-${item.id}`}
                            >
                              Browse interactions from {format(createdDate, "MMM d, yyyy")}
                            </Link>
                          </PopoverContent>
                        </Popover>
                      ) : linkUrl ? (
                        // Non-observation event with a destination — ExternalLink arrow
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link href={linkUrl} data-testid={`link-activity-${item.id}`}>
                              <ExternalLink className="h-3 w-3 text-muted-foreground/50 hover:text-foreground transition-colors shrink-0" />
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">{getItemLinkTooltip(item)}</TooltipContent>
                        </Tooltip>
                      ) : null}
                      {taskLinkUrl && (
                        // observation_added_to_task/meeting — also link to the task/meeting itself
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link href={taskLinkUrl} data-testid={`link-task-${item.id}`}>
                              <ExternalLink className="h-3 w-3 text-muted-foreground/50 hover:text-foreground transition-colors shrink-0" />
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            {item.eventType === "observation_added_to_meeting" ? "Open meeting" : "Open task"}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    {subtitle && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2" data-testid={`text-activity-task-name-${item.id}`}>
                        {subtitle}
                      </p>
                    )}
                    {item.message && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2" data-testid={`text-activity-message-${item.id}`}>
                        {item.message}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {item.statusName && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {item.statusName}
                        </Badge>
                      )}
                      {item.assigneeName && (
                        <span className="text-[10px] text-muted-foreground/70">
                          Assigned: {item.assigneeName}
                        </span>
                      )}
                      {item.dueDate && (
                        <span className="text-[10px] text-muted-foreground/70">
                          Due: {format(parseDateSafe(item.dueDate), "MMM d")}
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground/50" title={format(createdDate, "PPpp")}>
                        {formatActivityTimestamp(createdDate)}
                        {item.actorFirstName && (
                          <span className="text-muted-foreground/40"> · {item.actorFirstName}</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </Fragment>
            );
          })}
          {hasMore && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-xs text-muted-foreground ml-2">Loading more...</span>
            </div>
          )}
          {/* AP.3: history gap marker — shown when animal's intake predates BETI data */}
          {!hasMore && showGapMarker && (
            <HistoryGapMarker dataStartDate={dataStartDate!} />
          )}
        </div>
      </div>
    </div>
  );
}
